import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut, FileArrowDown, House, ImageSquare, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, adminHeaders, getSecret, fileUrl } from "@/lib/api";
import { Lightbox } from "@/components/Lightbox";
import { RefreshButton } from "@/components/RefreshButton";

export default function SiapKirimPage() {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [expandedRenders, setExpandedRenders] = useState(() => new Set());
    const toggleRenders = (id) =>
        setExpandedRenders((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });

    const load = useCallback(async () => {
        const res = await api.get("/admin/siap-kirim", { headers: adminHeaders() });
        setRooms(res.data);
    }, []);

    const selesaiInstall = async (roomId) => {
        setBusy(roomId);
        try {
            await api.patch(`/admin/rooms/${roomId}/production-status`, { value: "terkirim" }, { headers: adminHeaders() });
            toast.success("Barang ditandai terkirim");
            await load();
        } catch {
            toast.error("Gagal mengubah status");
        } finally {
            setBusy("");
        }
    };

    useEffect(() => {
        if (!getSecret()) {
            navigate("/admin");
            return;
        }
        load()
            .catch(() => toast.error("Gagal memuat daftar siap kirim"))
            .finally(() => setLoading(false));
    }, [load, navigate]);

    return (
        <div className="min-h-screen flex flex-col" data-testid="siap-kirim-page">
            <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto w-full px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="siap-kirim-back"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors"
                            title="Kembali"
                        >
                            <ArrowLeft size={20} weight="light" />
                        </button>
                        <img src="/logo.png" alt="INERRE interior" className="h-10 object-contain" />
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-muted-foreground">Siap Kirim</p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium">Semua Siap Kirim</h1>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <button
                            data-testid="siap-kirim-home-btn"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title="Kembali ke pemindai"
                        >
                            <House size={20} weight="light" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full max-w-6xl mx-auto px-6 py-6">
                {loading ? (
                    <p className="text-sm text-muted-foreground" data-testid="siap-kirim-loading">Memuat...</p>
                ) : rooms.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-16" data-testid="siap-kirim-empty">
                        Tidak ada ruangan dengan status "Siap Kirim".
                    </p>
                ) : (
                    <div className="space-y-6" data-testid="siap-kirim-list">
                        {rooms.map((room) => (
                            <div key={room.id} className="border border-border bg-white" data-testid={`siap-kirim-room-${room.id}`}>
                                <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div>
                                        <h3 className="font-serif text-xl sm:text-2xl tracking-tight font-medium" data-testid={`siap-kirim-name-${room.id}`}>
                                            {room.name}
                                        </h3>
                                        <p className="text-xs text-muted-foreground mt-1" data-testid={`siap-kirim-prop-${room.id}`}>
                                            {room.property_name}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2 sm:justify-end sm:shrink-0">
                                        <button
                                            data-testid={`siap-kirim-shipped-${room.id}`}
                                            onClick={() => selesaiInstall(room.id)}
                                            disabled={busy === room.id}
                                            className="flex-1 sm:flex-none text-center border border-primary text-primary px-3 py-2 text-xs hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                                        >
                                            Barang Terkirim
                                        </button>
                                        <a
                                            data-testid={`siap-kirim-open-${room.id}`}
                                            href={`/a/${room.id}`}
                                            className="flex-1 sm:flex-none justify-center border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                        >
                                            <ArrowSquareOut size={14} weight="light" /> Buka Ruangan
                                        </a>
                                    </div>
                                </div>

                                <div className="p-4 space-y-4">
                                    <div>
                                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Rendering</p>
                                        {room.renderings?.length > 0 ? (
                                            <>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`siap-kirim-renderings-${room.id}`}>
                                                {(expandedRenders.has(room.id) || room.renderings.length <= 4 ? room.renderings : room.renderings.slice(0, 3)).map((path, i) => (
                                                    <button
                                                        key={path}
                                                        onClick={() => setLightbox({ images: room.renderings, index: i })}
                                                        className="aspect-[4/3] bg-secondary overflow-hidden border border-border block cursor-zoom-in"
                                                        data-testid={`siap-kirim-rendering-${room.id}-${i}`}
                                                    >
                                                        <img
                                                            src={fileUrl(path)}
                                                            alt={`Rendering ${room.name} ${i + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                                {!expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                    <button
                                                        onClick={() => toggleRenders(room.id)}
                                                        data-testid={`siap-kirim-more-renderings-${room.id}`}
                                                        className="aspect-[4/3] border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                                                    >
                                                        +{room.renderings.length - 3} lainnya
                                                    </button>
                                                )}
                                            </div>
                                            {expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                <button
                                                    onClick={() => toggleRenders(room.id)}
                                                    data-testid={`siap-kirim-less-renderings-${room.id}`}
                                                    className="text-xs text-primary hover:underline mt-2"
                                                >
                                                    Sembunyikan
                                                </button>
                                            )}
                                            </>
                                        ) : (
                                            <div className="aspect-[4/3] max-w-xs bg-secondary flex items-center justify-center border border-border" data-testid={`siap-kirim-no-rendering-${room.id}`}>
                                                <ImageSquare size={28} weight="light" className="text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {room.me_link && (
                                            <a
                                                data-testid={`siap-kirim-me-${room.id}`}
                                                href={room.me_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                            >
                                                <Lightning size={14} weight="light" /> Gambar ME / Electrical
                                            </a>
                                        )}
                                        {room.gambar_kerja_link && (
                                            <a
                                                data-testid={`siap-kirim-gk-${room.id}`}
                                                href={room.gambar_kerja_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                            >
                                                <FileArrowDown size={14} weight="light" /> Gambar Kerja
                                                <span
                                                    className="ml-1 text-[10px] font-bold tracking-widest uppercase border border-primary text-primary px-1.5 py-0.5"
                                                    data-testid={`siap-kirim-gk-version-${room.id}`}
                                                >
                                                    {room.gambar_kerja_version || "Tanpa versi"}
                                                </span>
                                            </a>
                                        )}
                                        {!room.me_link && !room.gambar_kerja_link && (
                                            <p className="text-xs text-muted-foreground" data-testid={`siap-kirim-no-links-${room.id}`}>
                                                Belum ada link Google Drive.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {lightbox && (
                <Lightbox
                    src={fileUrl(lightbox.images[lightbox.index])}
                    alt="Rendering"
                    onClose={() => setLightbox(null)}
                    onPrev={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length })) : null}
                    onNext={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length })) : null}
                />
            )}
        </div>
    );
}
